import Typography from '~/@/components/ui/typography'
import { useGetPreviewCellsQuery } from '~/common/generated/apollo-operations'
import { DetailedTable } from '~/components/tables/types'
import { useTypedTranslation } from '../../../common/hooks/useTypedTranslation'
import { LookupColumnPreviewCell } from './LookupColumnPreviewCell'
import { AllTablesQueryTableColumn } from './types'

interface LookupColumnPreviewDataProps {
  highlightedOption: AllTablesQueryTableColumn | null
  selectedOption?: AllTablesQueryTableColumn
  showDisabledState: boolean
  lookupTable?: Pick<DetailedTable, 'id'> | null
}

/**
 * Renders the preview data for a column that can be used as a lookup
 */
export function LookupColumnPreviewData({
  highlightedOption,
  showDisabledState,
  selectedOption,
  lookupTable,
}: LookupColumnPreviewDataProps) {
  const { t } = useTypedTranslation()
  // If no option is highlighted, show the preview data for the selected option, if any
  const currentColumn = highlightedOption || selectedOption

  const { data: previewCellsData } = useGetPreviewCellsQuery({
    variables: {
      input: {
        tableId: lookupTable?.id ?? '',
        tableColumnId: currentColumn?.id ?? '',
        limit: 5,
      },
    },
    skip: !currentColumn || !lookupTable,
  })

  const previewData = previewCellsData?.previewCells

  if (!currentColumn || !previewData) {
    return <div className="flex w-1/2 grow flex-col border-s border-gray-4"></div>
  }

  return (
    <div className="flex w-1/2 grow flex-col border-s border-gray-4">
      <div className="bg-gray-1 p-2">{t('table_column.preview_data')}</div>
      <div className="flex grow flex-col overflow-hidden">
        {previewData.length === 0 ? (
          <div className="flex grow items-center justify-center">
            <Typography variant="body3">{t('table_column.no_preview_data_available')}</Typography>
          </div>
        ) : (
          <div className="flex grow flex-col">
            {previewData.map((cell) => {
              return (
                <LookupColumnPreviewCell
                  key={cell.id}
                  cell={cell}
                  column={currentColumn}
                  showDisabledState={showDisabledState}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
